/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'hooks/use-mobile';
import DataTable from 'components/blocks/data-table/data-table';
import { FileType, IFileTrashData, PaginationState } from '../../utils/file-manager';
import { TrashTableColumns } from './trash-files-table-columns';
import { TrashDetailsSheet } from './trash-files-details';
import { SortingState } from '@tanstack/react-table';
import { useTrashedFilesQuery } from '../../hooks/use-document-manager';
import { useQueryClient } from '@tanstack/react-query';
import { getFile } from 'services/storage-service';
import { useConfig } from 'hooks/use-config';
import PDFViewer from '../pdf-viewer';

interface TrashFilesListViewProps {
  onRestore: (file: IFileTrashData) => Promise<void>;
  onDelete: (file: IFileTrashData) => Promise<void>;
  filters: {
    name?: string;
    fileType?: FileType;
    trashedDate?: {
      from?: Date;
      to?: Date;
    };
  };
  deletedItemIds?: Set<string>;
  restoredItemIds?: Set<string>;
  handleSetAllFileIds?: (fileIds: string[]) => void;
}

export const TrashFilesListView: React.FC<TrashFilesListViewProps> = ({
  onRestore,
  onDelete,
  filters,
  handleSetAllFileIds
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { configurationSettings } = useConfig();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<IFileTrashData | null>(null);

  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
    totalCount: 0,
  });
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const queryParams = useMemo(() => {
    return {
      page: paginationState.pageIndex,
      pageSize: paginationState.pageSize,
      filter: {
        name: filters.name ?? undefined,
        fileType: filters.fileType ?? undefined,
        trashedDate: filters.trashedDate ?? undefined,
      },
      sort: sortingState,
    };
  }, [
    paginationState.pageIndex,
    paginationState.pageSize,
    filters.name,
    filters.fileType,
    filters.trashedDate,
    sortingState,
  ]);

  const { data, isLoading, error } = useTrashedFilesQuery(queryParams, { softDeleted: true });

  useEffect(() => {
    if (data?.data) {
      handleSetAllFileIds?.(data.data.map((file) => file.id));
    }
  }, [data?.data, handleSetAllFileIds]);

  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPaginationState((prev) => ({
        ...prev,
        pageIndex: newPagination.pageIndex,
        pageSize: newPagination.pageSize,
      }));
    },
    []
  );

  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSortingState(newSorting);
  }, []);

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setPaginationState((prev) => ({
        ...prev,
        totalCount: data.totalCount,
      }));
    }
  }, [data?.totalCount]);

  useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [filters]);

  const handleViewDetailsWrapper = useCallback((file: IFileTrashData) => {
    setSelectedFile(file);
    setIsDetailsOpen(true);
  }, []);

  const handleRestoreWrapper = useCallback(
    async (file: IFileTrashData) => {
      setSelectedFile(file);
      try {
        await onRestore(file);
        queryClient.invalidateQueries({ queryKey: ['trashed-files'] });
      } catch (error) {
        console.error('Error restoring file:', error);
      }
    },
    [onRestore]
  );

  const handleDeleteWrapper = useCallback(
    async (file: IFileTrashData) => {
      setSelectedFile(file);
      try {
        await onDelete(file);
        queryClient.invalidateQueries({ queryKey: ['trashed-files'] });
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    },
    [onDelete]
  );

  const handleCloseDetails = useCallback(() => {
    setIsDetailsOpen(false);
    setSelectedFile(null);
  }, []);

  const columns = useMemo(() => {
    return TrashTableColumns({
      onRestore: handleRestoreWrapper,
      onDelete: handleDeleteWrapper,
      t,
    });
  }, [handleRestoreWrapper, handleDeleteWrapper, t]);

  const paginationProps = useMemo(() => {
    return {
      pageIndex: paginationState.pageIndex,
      pageSize: paginationState.pageSize,
      totalCount: data?.totalCount ?? 0,
      manualPagination: true,
    };
  }, [paginationState]);

  if (error) {
    return <div className="p-4 text-error">{t('ERROR_LOADING_TRASH_FILES')}</div>;
  }

  const shouldHideMainContent = isMobile && isDetailsOpen;

  async function getFileUrl(fileId: any): Promise<string | undefined> {
    const response = await getFile(fileId.toString(), configurationSettings, undefined);
    return response?.url;
  }

  const onDoubleClick = async (fileId: any) => {
    if (!fileId) return;
    const Url = await getFileUrl(fileId);
    if (!Url) return;
    setFileUrl(Url);
  };

  return (
    <div className="flex h-full w-full rounded-xl relative">
      {!shouldHideMainContent && (
        <div
          className={`flex flex-col h-full transition-all duration-300 ${isDetailsOpen && !isMobile ? 'sm:w-2/3 md:w-2/3 lg:w-2/3 xl:w-3/4 2xl:w-4/5' : 'w-full'
            }`}
        >
          <div className="h-full  overflow-y-auto flex-col flex w-full gap-6 md:gap-8">
            <DataTable
              data={data?.data ?? []}
              columns={columns}
              onRowClick={handleViewDetailsWrapper}
              isLoading={isLoading}
              pagination={{
                pageIndex: paginationProps.pageIndex,
                pageSize: paginationProps.pageSize,
                totalCount: paginationProps.totalCount,
              }}
              onPaginationChange={handlePaginationChange}
              onSortingChange={handleSortingChange}
              manualPagination={paginationProps.manualPagination}
              mobileColumns={['name']}
              expandable={true}
              onDoubleClick={onDoubleClick}
            />
          </div>

          {fileUrl && (
            <PDFViewer url={fileUrl} setUrl={setFileUrl} fileType={selectedFile?.fileType} />
          )}
        </div>
      )}

      <div>
        <TrashDetailsSheet
          isOpen={isDetailsOpen}
          onClose={handleCloseDetails}
          file={
            selectedFile
              ? {
                ...selectedFile,
                lastModified:
                  selectedFile.trashedDate ?? new Date(selectedFile.trashedDate ?? Date.now()),
                isShared: selectedFile.isShared ?? false,
              }
              : null
          }
          t={t}
        />
      </div>
    </div>
  );
};
